import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Truck, AlertTriangle, CheckCircle2, Filter } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

// Phase E (Feb 2026): Stock-movement report for Management + Super Admin.
// Shows totals (loaded / sold / returned / mismatch) + per-record breakdown.
// Filters: date range, salesman, district, depot, product.
export default function StockReport() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [records, setRecords] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    salesman_id: "",
    district_id: "",
    product_id: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v)
      );
      const [r, sm, dt, pr] = await Promise.all([
        api.get("/stock", { params }),
        salesmen.length === 0 ? api.get("/users", { params: { role: "salesman" } }) : Promise.resolve({ data: salesmen }),
        districts.length === 0 ? api.get("/districts").catch(() => ({ data: [] })) : Promise.resolve({ data: districts }),
        products.length === 0 ? api.get("/products") : Promise.resolve({ data: products }),
      ]);
      setRecords(r.data || []);
      if (salesmen.length === 0) setSalesmen(sm.data || []);
      if (districts.length === 0) setDistricts(dt.data || []);
      if (products.length === 0) setProducts(pr.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  // Aggregate top-line totals
  const totals = records.reduce((acc, r) => {
    acc.loaded += r.totals?.loaded || 0;
    acc.sold += r.totals?.sold || 0;
    acc.returned += r.totals?.returned || 0;
    acc.mismatch += r.totals?.mismatch || 0;
    return acc;
  }, { loaded: 0, sold: 0, returned: 0, mismatch: 0 });

  // Filter selectively for product breakdown
  const productAggregate = (() => {
    const map = {};
    for (const r of records) {
      for (const it of (r.items || [])) {
        if (!map[it.product_id]) {
          map[it.product_id] = {
            product_id: it.product_id,
            name: lang === "ta" ? (it.product_name_ta || it.product_name) : it.product_name,
            loaded: 0, sold: 0, returned: 0,
          };
        }
        map[it.product_id].loaded += it.loaded_qty || 0;
        map[it.product_id].sold += it.sold_qty || 0;
        map[it.product_id].returned += (it.returned_qty || 0);
      }
    }
    return Object.values(map).sort((a, b) => b.loaded - a.loaded);
  })();

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={lang === "ta" ? "ஸ்டாக் அறிக்கை" : "Stock Report"} onBack={() => nav("/management")} />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Filters */}
        <div className="bg-white rounded-2xl p-5 soft-shadow space-y-3" data-testid="sr-filters">
          <div className="flex items-center gap-2 mb-1">
            <Filter className="w-4 h-4 text-[#86868B]" />
            <div className="heading-font font-bold text-base text-[#1D1D1F]">{lang === "ta" ? "வடிகட்டி" : "Filters"}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <FilterField label={lang === "ta" ? "தொடக்கம்" : "From"} type="date" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} testid="sr-date-from" />
            <FilterField label={lang === "ta" ? "முடிவு" : "To"} type="date" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} testid="sr-date-to" />
            <FilterField label={t("top_salesman")} type="select" options={[{value:"",label:"—"}, ...salesmen.map(s=>({value:s.id,label:s.name}))]} value={filters.salesman_id} onChange={(v) => setFilters({ ...filters, salesman_id: v })} testid="sr-salesman" />
            <FilterField label="District" type="select" options={[{value:"",label:"—"}, ...districts.map(d=>({value:d.id,label:d.name_en || d.name}))]} value={filters.district_id} onChange={(v) => setFilters({ ...filters, district_id: v })} testid="sr-district" />
            <FilterField label={t("products")} type="select" options={[{value:"",label:"—"}, ...products.map(p=>({value:p.id,label:p.name_en || p.name}))]} value={filters.product_id} onChange={(v) => setFilters({ ...filters, product_id: v })} testid="sr-product" />
            <button
              onClick={() => setFilters({ date_from: "", date_to: "", salesman_id: "", district_id: "", product_id: "" })}
              data-testid="sr-clear"
              className="self-end bg-white border border-gray-200 hover:bg-gray-50 rounded-2xl px-4 py-3 text-sm font-bold text-[#86868B]"
            >
              {lang === "ta" ? "அழி" : "Clear"}
            </button>
          </div>
        </div>

        {/* Top-line totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sr-totals">
          <TotalCard icon={Truck}        color="bg-[#0055FF]" label={lang === "ta" ? "மொத்த ஏற்றம்" : "Loaded"}   value={totals.loaded} />
          <TotalCard icon={CheckCircle2} color="bg-[#34C759]" label={lang === "ta" ? "மொத்த விற்பனை" : "Sold"}     value={totals.sold} />
          <TotalCard icon={Boxes}        color="bg-[#FF9500]" label={lang === "ta" ? "மொத்த திரும்பம்" : "Returned"} value={totals.returned} />
          <TotalCard icon={AlertTriangle} color={totals.mismatch === 0 ? "bg-[#5856D6]" : "bg-[#FF3B30]"} label={lang === "ta" ? "வேறுபாடு" : "Mismatch"} value={(totals.mismatch >= 0 ? "+" : "") + totals.mismatch} />
        </div>

        {/* Product-wise aggregate */}
        {productAggregate.length > 0 && (
          <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="sr-product-table">
            <div className="p-5 border-b border-gray-100">
              <h2 className="heading-font font-bold text-lg">{lang === "ta" ? "பொருள் வாரியா" : "Product-wise Movement"}</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "ஏற்றம்" : "Loaded"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "விற்றது" : "Sold"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "திரும்பம்" : "Returned"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {productAggregate.map((p) => (
                  <tr key={p.product_id} className="hover:bg-gray-50/40" data-testid={`sr-product-${p.product_id}`}>
                    <td className="px-4 py-3 font-semibold heading-font">{p.name}</td>
                    <td className="px-4 py-3"><span className="pill bg-blue-50 text-blue-700">{p.loaded}</span></td>
                    <td className="px-4 py-3"><span className="pill bg-green-50 text-green-700">{p.sold}</span></td>
                    <td className="px-4 py-3"><span className="pill bg-orange-50 text-orange-700">{p.returned}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Per-record list */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="sr-records-table">
          <div className="p-5 border-b border-gray-100">
            <h2 className="heading-font font-bold text-lg">{lang === "ta" ? "ஒவ்வொரு பதிவும்" : "Records"} ({records.length})</h2>
          </div>
          {loading ? (
            <div className="p-8 text-center text-[#86868B]">{t("loading")}</div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center text-[#86868B]" data-testid="sr-empty">
              {lang === "ta" ? "தரவு இல்ல" : "No data"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/60">
                  <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                    <th className="px-4 py-3">{lang === "ta" ? "தேதி" : "Date"}</th>
                    <th className="px-4 py-3">{t("name")}</th>
                    <th className="px-4 py-3">{lang === "ta" ? "டிப்போ" : "Depot"}</th>
                    <th className="px-4 py-3">L</th>
                    <th className="px-4 py-3">S</th>
                    <th className="px-4 py-3">R</th>
                    <th className="px-4 py-3">±</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50/40" data-testid={`sr-record-${r.id}`}>
                      <td className="px-4 py-3 text-xs text-[#86868B]">{r.date}</td>
                      <td className="px-4 py-3 font-semibold heading-font">
                        {r.salesman_name}
                        {r.salesman_role === "manager" && <span className="pill bg-blue-50 text-blue-700 text-[10px] ml-1">M</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#86868B]">{r.depot_name || "—"}</td>
                      <td className="px-4 py-3"><span className="pill bg-blue-50 text-blue-700">{r.totals?.loaded || 0}</span></td>
                      <td className="px-4 py-3"><span className="pill bg-green-50 text-green-700">{r.totals?.sold || 0}</span></td>
                      <td className="px-4 py-3">{r.totals?.returned == null ? "—" : <span className="pill bg-orange-50 text-orange-700">{r.totals.returned}</span>}</td>
                      <td className="px-4 py-3">
                        {r.totals?.mismatch == null ? "—" : (
                          <span className={`pill ${(r.totals.mismatch || 0) === 0 ? "bg-gray-100 text-gray-700" : (r.totals.mismatch || 0) < 0 ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"}`}>
                            {r.totals.mismatch >= 0 ? "+" : ""}{r.totals.mismatch}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TotalCard = ({ icon: Icon, color, label, value }) => (
  <div className="bg-white rounded-2xl p-4 soft-shadow">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{label}</div>
        <div className="heading-font text-2xl font-bold text-[#1D1D1F]">{value}</div>
      </div>
    </div>
  </div>
);

const FilterField = ({ label, type, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold mb-1.5 block">{label}</label>
    {type === "select" ? (
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#F5F5F7] rounded-2xl px-3 py-3 text-sm font-semibold text-[#1D1D1F] focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none transition"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F" }}
        className="w-full bg-[#F5F5F7] rounded-2xl px-3 py-3 text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none transition"
      />
    )}
  </div>
);
